import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import {
    showGameDetectedNotification,
    hideGameDetectedNotification,
    showGameExitNotification,
    hideGameExitNotification,
} from "@/redux/meta/metaActions";
import { useGameNotificationPreferences } from "./useGameNotificationPreferences";
import { overwolfGameInfo } from "@/hooks/useOverwolfInterop";

/**
 * Hook that monitors Overwolf game state changes and manages notification display
 * This is the core logic that connects game detection to the notification system
 */
export const useGameNotificationManager = () => {
    const dispatch = useDispatch();
    const gameInfo = useSelector((store: IStore) => store.metaStore.overwolfGameInfo);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const gameNotifications = useSelector((store: IStore) => store.metaStore.gameNotifications);
    const {
        shouldShowGameDetectedNotification,
        shouldShowGameExitNotification,
        shouldAutoEnterGameMode,
    } = useGameNotificationPreferences();

    /*
     * Track previous game state to detect changes
     * Initialize with current game info to prevent false "game detected" on mount
     */
    const prevGameInfoRef = useRef<overwolfGameInfo | null>(gameInfo);
    const sessionStartTimeRef = useRef<number | null>(null);

    // Use sessionStorage to track if ANY notification was dismissed this session
    const NOTIFICATION_DISMISSED_KEY = "gameDetectedNotificationDismissed";

    // Helper functions for sessionStorage tracking
    const hasNotificationBeenDismissedThisSession = () => {
        // If user dismissed ANY game notification this session, don't show any more
        return sessionStorage.getItem(NOTIFICATION_DISMISSED_KEY) === "true";
    };

    const markNotificationDismissed = () => {
        // Mark that user has dismissed a notification this session
        sessionStorage.setItem(NOTIFICATION_DISMISSED_KEY, "true");
    };

    const clearNotificationTracking = () => {
        sessionStorage.removeItem(NOTIFICATION_DISMISSED_KEY);
    };

    // Initialize session tracking if game is already running on mount
    useEffect(() => {
        if (gameInfo?.isGameRunning && !sessionStartTimeRef.current) {
            /*
             * Game is already running when component mounts (e.g., after window mode switch)
             * Set a session start time to prevent incorrect session duration calculations
             */
            sessionStartTimeRef.current = Date.now();
        }
    }, []); // Only run on mount

    useEffect(() => {
        if (!isOnOverwolf) {
            // Only dispatch hide actions if notifications are actually visible to prevent infinite loops
            if (gameNotifications.gameDetectedVisible) {
                dispatch(hideGameDetectedNotification());
            }
            if (gameNotifications.gameExitVisible) {
                dispatch(hideGameExitNotification());
            }
            return;
        }

        const prevGameInfo = prevGameInfoRef.current;
        const currentGameInfo = gameInfo;

        // Game Detection Logic
        if (!prevGameInfo?.isGameRunning && currentGameInfo?.isGameRunning) {
            // Game was just detected

            // Record session start time
            sessionStartTimeRef.current = Date.now();

            // Check if we should show notification or auto-enter
            if (shouldAutoEnterGameMode(currentGameInfo.gameId)) {
                // TODO: Trigger auto-enter game mode (will be implemented in CockpitV3 update)
            } else if (
                shouldShowGameDetectedNotification(currentGameInfo.gameId.toString()) &&
                !gameNotifications.gameDetectedVisible &&
                !hasNotificationBeenDismissedThisSession()
            ) {
                // Additional check: Don't show if we're already in game-specific view
                const urlParams = new URLSearchParams(window.location.search);
                const isInGameView = urlParams.get("view") === "games";

                if (!isInGameView) {
                    dispatch(showGameDetectedNotification(currentGameInfo));
                } else {
                    // Already in game view, skip notification
                }
            }
        }

        // Game Exit Logic
        if (prevGameInfo?.isGameRunning && !currentGameInfo?.isGameRunning) {
            // Game was just exited

            // Hide game detected notification if it was showing
            dispatch(hideGameDetectedNotification());

            /*
             * Automatically switch back to desktop view when game ends
             * Check URL parameter to see if we're in compact/mobile mode
             */
            const urlParams = new URLSearchParams(window.location.search);
            const isInCompactMode = urlParams.get("alwaysMobile") === "true" || sessionStorage.getItem("alwaysMobile") === "true";

            if (isInCompactMode) {
                // Game ended - automatically switch back to desktop view
                import("@/hooks/useOverwolfInterop").then(({ sendMessageToOW }) => {
                    sendMessageToOW({
                        type: "de:request-window-resize",
                        payload: {
                            mode: "expanded",
                        },
                    });
                    // Clear the compact mode flag
                    sessionStorage.removeItem("alwaysMobile");
                });
            }

            // Calculate session stats
            const sessionDuration = sessionStartTimeRef.current
                ? Math.round((Date.now() - sessionStartTimeRef.current) / 1000 / 60) // minutes
                : undefined;

            /*
             * DISABLED: Game exit notification is redundant since we auto-switch to desktop view
             * if (shouldShowGameExitNotification(prevGameInfo.gameId)) {
             *     console.log('[GameNotificationManager] Showing game exit notification for:', prevGameInfo.gameTitle);
             *
             *     // TODO: Calculate XP earned and quests completed during session
             *     // This would require tracking these values during the session
             *     const sessionStats = {
             *         sessionDuration,
             *         xpEarned: undefined, // TODO: Implement XP tracking
             *         questsCompleted: undefined, // TODO: Implement quest completion tracking
             *     };
             *
             *     dispatch(showGameExitNotification(prevGameInfo, sessionStats));
             * }
             */

            // Reset session tracking
            sessionStartTimeRef.current = null;
            clearNotificationTracking();
        }

        // Game State Changes (while game is running)
        if (prevGameInfo?.isGameRunning && currentGameInfo?.isGameRunning) {
            // Check for significant state changes that might affect notifications
            const prevGepStarted = prevGameInfo.gepStarted;
            const currentGepStarted = currentGameInfo.gepStarted;

            if (!prevGepStarted && currentGepStarted) {
                // GEP just started - might want to hide any "connecting" states in notifications
            }

            if (prevGepStarted && !currentGepStarted) {
                // GEP stopped - might want to show degraded state
            }
        }

        // Update previous game info reference
        prevGameInfoRef.current = currentGameInfo;
    }, [
        gameInfo,
        isOnOverwolf,
        dispatch,
        gameNotifications,
        shouldShowGameDetectedNotification,
        shouldShowGameExitNotification,
        shouldAutoEnterGameMode,
    ]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Hide all notifications when component unmounts
            dispatch(hideGameDetectedNotification());
            dispatch(hideGameExitNotification());
        };
    }, [dispatch]);

    return {
        // Return current state for debugging/monitoring
        currentGameInfo: gameInfo,
        sessionDuration: sessionStartTimeRef.current
            ? Math.round((Date.now() - sessionStartTimeRef.current) / 1000 / 60)
            : undefined,
        markNotificationDismissed, // Export this so components can mark dismissal
    };
};